// USA
export const locale = {
  lang: "en",
  data: {
    APP_NAME: "Wise",
    TRANSLATOR: {
      SELECT: "Select your language",
    },
    QUIZ: {
      SEARCH_TITLE: "What do you want to practise? Try a search.",
      SEARCH_PLACEHOLDER: "Search a practice set or a course",
      SEARCH_CLEAR: "Clear search",
      SEARCH_EMPTY: "No practice set matches that search",
      EMPTY_HINT: "Practice sets appear here once you are enrolled on a course that has them.",
      MY_DRILLS: "My drills",
      MY_DRILLS_HINT: "Built from the topics you are still weak on — retake any of them for a fresh set of questions",
      CONTINUE_DRILL: "Continue",
      TITLE: "Quizzes",
      RESULT: "Quiz result",
      HISTORY: "Quiz history",
      MY_SKILLS: "My skills",
      CHILD_SKILLS: "Student skills",
      EMPTY: "No quizzes yet for the courses you are enrolled in",
      NONE_FOR_COURSE: "This course has no quizzes yet",
      NO_HISTORY: "You have not taken a quiz yet",
      NO_HISTORY_HINT: "Finish one practice set and the results will appear here, with the key and a note on every question.",
      GO_PRACTISE: "Go to practice",
      NO_SKILL_DATA: "Take a quiz first and we will show you what you are strong at and what to review",
      QUESTION: "Question",
      QUESTION_COUNT: "Questions",
      PASS_PERCENT: "Pass mark",
      START: "Start",
      RETAKE: "Take again",
      NO_RETAKE: "One attempt only",
      BEST_RESULT: "Best result",
      VIEW_RESULT: "View result",
      KIND_PRACTICE: "Topic drill",
      NEXT: "Next",
      BACK: "Back",
      TO_REVIEW: "Review",
      REVIEW: "Review before submitting",
      UNANSWERED: "Still unanswered:",
      ITEMS: "",
      ALL_ANSWERED: "All questions answered",
      SUBMIT: "Submit answers",
      SUBMITTING: "Submitting…",
      TYPE_ANSWER: "Type your answer",
      CORRECT: "Correct",
      PASSED: "Passed",
      NOT_PASSED: "Not passed",
      BY_TOPIC: "By topic",
      BY_DIFFICULTY: "By difficulty",
      ANSWER_KEY: "Answers and explanations",
      MISCONCEPTION: "Why it was wrong",
      REPEATED_MISTAKES: "Mistakes you keep making",
      REPEATED_MISTAKES_HINT: "The same mistake more than once means the idea is still wrong, not that you slipped",
      REVIEW_DUE: "Time to revisit",
      REVIEW_DUE_HINT: "You could do these, but it has been a while",
      REVIEW_BADGE: "Due for review",
      YOUR_ANSWER: "Your answer",
      CORRECT_ANSWER: "Correct answer",
      NO_ANSWER: "Not answered",
      EXPLANATION: "Explanation",
      BACK_TO_LIST: "Back to quizzes",
      SCORE: "Score",
      ANSWERED: "Questions answered",
      STRONG_TOPICS: "Strong topics",
      WEAK_TOPICS: "Topics to review",
      IMPROVING: "Improving",
      WHAT_NEXT: "What to review next",
      SUMMARY: "In short",
      SUMMARY_AUTO: "Assembled automatically from practice results",
      START_HERE: "Start here",
      NEEDS_FIRST: "Needs this first:",
      ABILITY: "Ability",
      ABILITY_HINT: "Chance of answering a medium question correctly, adjusted for how hard the questions answered so far were",
      LEVEL: "Level",
      LEVEL_RANGE: "between",
      LEVEL_HINT: "The same scale the questions use — being at a level means about an even chance on a question at that level",
      BY_LEVEL: "By question level",
      SUGGESTED_COURSES: "Courses that would help",
      ALREADY_ENROLLED: "Already enrolled",
      VIEW_COURSE: "View course",
      FOR_STUDENT: "Report for",
      REWATCH: "Rewatch video",
      PRACTISE: "Practise this",
      BUILDING: "Building…",
    },
    FOOTER: {
      BLURB: "An online learning platform that does not stop at video — practice sets, per-student skill reports, and a clear answer to what to review next.",
      EXPLORE: "Get started",
      COMPANY: "Company",
      CONTACT: "Contact",
      DEMO_NOTE: "Demo system — every record, image and payment here is sample data, not the real thing.",
      RIGHTS: "All rights reserved",
      LEGAL: {
        PRIVACY: "Privacy Policy",
        TERMS: "Terms & Conditions",
        COOKIES: "Cookies Policy",
        PAYMENT: "Payment & Promotion Policy",
      },
    },
    LEGAL: {
      UPDATED: "Last updated 1 September 2025",
      DEMO_NOTE:
        "This is sample policy text for a demo platform. It is not legal advice and does not govern a real service.",
      PRIVACY: {
        TITLE: "Privacy Policy",
        INTRO:
          "This policy explains what personal data Wise collects when you use this demo, why we collect it, and the choices you have. Because this is a demo, all accounts and records are seeded sample data.",
        S1: {
          HEAD: "Information we collect",
          P1: "When you create an account or use the one-click demo sign-in, we store the name and email address on that account together with the courses and practice sets you open.",
          P2: "We also collect basic technical data — browser type, device, and pages visited — so the demo can be kept working and secure.",
        },
        S2: {
          HEAD: "How we use your information",
          P1: "Data is used to run the platform: to sign you in, show your progress and skill reports, and remember your preferences such as language.",
          P2: "We do not sell personal data. In a customer deployment, data is processed only to deliver the learning service and to meet legal and accounting obligations.",
        },
        S3: {
          HEAD: "Your rights and contact",
          P1: "You may request a copy of your data, ask for it to be corrected, or ask for the account to be deleted. In this demo, seeded accounts are reset periodically.",
          P2: "For any privacy request, contact the operator using the details on the Contact section of the site.",
        },
      },
      TERMS: {
        TITLE: "Terms & Conditions",
        INTRO:
          "These terms govern your use of the Wise platform. By creating an account or using the demo you agree to them.",
        S1: {
          HEAD: "Acceptance of terms",
          P1: "If you do not agree with any part of these terms, please do not use the platform.",
          P2: "We may update these terms as the service changes; continued use after an update means you accept the revised terms.",
        },
        S2: {
          HEAD: "Accounts and acceptable use",
          P1: "You are responsible for activity under your account and for keeping your password secure. Demo accounts are shared sample accounts and should not hold private information.",
          P2: "You agree not to disrupt the service, attempt to access other users' data, or copy course content for redistribution.",
        },
        S3: {
          HEAD: "Intellectual property and liability",
          P1: "Course materials, text, and interface belong to Wise or its licensors and may not be reproduced without permission.",
          P2: "The demo is provided \"as is\", without warranty, and to the extent permitted by law we are not liable for any loss arising from its use.",
        },
      },
      COOKIES: {
        TITLE: "Cookies Policy",
        INTRO:
          "This policy describes the cookies and similar storage this site uses and how you can control them.",
        S1: {
          HEAD: "What cookies are",
          P1: "Cookies are small files a site stores in your browser. Similar technologies include local storage, which this site uses to keep you signed in and remember your language.",
          P2: "Some storage is required for the site to function; other storage is optional.",
        },
        S2: {
          HEAD: "Cookies we use",
          P1: "Essential: session and authentication storage that keeps you signed in and protects forms.",
          P2: "Preferences: your chosen language and interface settings. This demo does not run third-party advertising or analytics cookies.",
        },
        S3: {
          HEAD: "Managing cookies",
          P1: "You can clear or block cookies in your browser settings. Blocking essential cookies will stop you from signing in.",
          P2: "Clearing storage will sign you out and reset preferences such as language.",
        },
      },
      PAYMENT: {
        TITLE: "Payment & Promotion Policy",
        INTRO:
          "This policy covers payment, receipts, refunds, and promotions. In this demo, checkout is fully simulated — no payment is taken and no money moves.",
        S1: {
          HEAD: "Payment and receipts",
          P1: "In a live deployment, course prices are shown in Thai Baht and include applicable tax. Access is granted once payment is confirmed.",
          P2: "A receipt or tax invoice is issued to the email on the account after a successful payment.",
        },
        S2: {
          HEAD: "Refunds and cancellation",
          P1: "A paid course may be refunded within 7 days of purchase provided no more than 20% of its lessons have been viewed.",
          P2: "Refunds are returned to the original payment method. Promotional credit and free courses are not refundable.",
        },
        S3: {
          HEAD: "Promotions and discount codes",
          P1: "Discount codes apply to eligible courses only, cannot be combined, and have no cash value.",
          P2: "We may change or end a promotion at any time; orders already confirmed keep the price they were placed at.",
        },
      },
    },
    MENU: {
      HOME: "Home",
      NEW: "new",
      ACTIONS: "Actions",
      CREATE_POST: "Create New Post",
      PAGES: "Pages",
      FEATURES: "Features",
      APPS: "Apps",
      DASHBOARD: "Dashboard",
      MONITOR: "Monitor",
      PROFILE: "Profile",
      PASSWORD: "Password",
      PARENT_MENU: "Parent Menu",
      COMPONENT: "Component",
      ABOUT_US: "About US",
      EXAMS: "Exams",
      EXAMINATION: "Examination",
      EXAMINATION_RESULT: "Examination Result",
      COURSES: "Courses",
      COURSE_DETAIL: "Course Detail",

      ONLINE_CLASS: "Online Class",
      PARENT_MONITOR: "My child's learning",
      PARENT_COURSES: "Course library",
      PARENT_STUDENTS: "Students",
      BLOG: "Blog",
      MANUAL: "User manual",
      MANUAL_READ: "Read the manual",
      MANUAL_VIDEO: "Video guides",
    },
    USER_MENU: {
      PROFILE: "Profile",
      PURCHASE_ORDER: "Purchase Order",
      SIGN_IN: "Sign in",
      SIGN_OUT: "Sign out",
      CLASS_ROOM: "Class Room",
    },

    MONITOR: {
        PROGRESS: "Progress",
        PROGRESS_EMPTY_HINT: "Once a course is enrolled, its progress appears here.",
    },
    APP: {
      NO_PARENT_LINKED: "No parent linked yet",
      NO_STUDENT_LINKED: "No student linked yet",
      NO_CHILDREN: "No students in your care yet",
      NO_CHILDREN_HINT: "Add a student with the invitation code from their account to follow their study and give them courses.",
      NOTHING_TO_PAY: "This course is free — you can start straight away.",
      BLOG_EMPTY: "No matching article",
      BLOG_EMPTY_HINT: "Try another word, or pick a category on the left.",
      PARENT_COURSES_EMPTY: "No courses in your library",
      PARENT_COURSES_EMPTY_HINT: "Try another search, or buy a course to give to a student in your care.",
      NEED_BANK: "Choose the bank you transferred to before confirming.",
      NEED_SLIP: "Attach the transfer slip before confirming.",
      CHOOSE_BANK: "Choose the bank you transferred to",
      GIVE_COURSE_FAILED: "Could not give the course. Please try again.",
      ALL: "All",
      PARENT_LIST: "Parents",
      PARENT_LIST_HINT: "Links a student account with a parent account.",
      STUDENT_LIST: "Students in your care",
      STUDENT_LIST_HINT: "Add a student with their invitation code to follow their study and give them courses.",
      COURSE_NOT_FOUND: "Course not found",
      COURSE_NOT_FOUND_HINT: "It may have been withdrawn, or the link is wrong. Browse the catalogue instead.",
      BLOG_NOT_FOUND: "Article not found",
      BLOG_NOT_FOUND_HINT: "It may have been removed, or the link is wrong. Try another article.",
      CATEGORY_EMPTY: "No courses in this category yet",
      CATEGORY_EMPTY_HINT: "Try another category, or browse everything.",
      ORDER_NOT_FOUND: "Order not found",
      ORDER_NOT_FOUND_HINT: "It may have been cancelled, or the link is wrong.",
      NO_COURSE_YET: "No courses in your classroom yet",
      NO_COURSE_YET_HINT: "Pick a course and start; everything you enrol on appears here.",
      SHARE_COURSE: "Share course",
      COPY_LINK: "Copy link",
      COPY_LINK_DONE: "Link copied",
      LOAD_ERROR: "Couldn't load the list. Please try again.",
      SEARCH_ORDER: "Search order number",
      SEARCH_MY_COURSE: "Search my courses",
      SEARCH_MY_COURSE_EMPTY: "No matching course",
      PROFILE_MENU: "Account menu",
      COPY_INVITE_CODE: "Copy invitation code",
      ADD_STUDENT_BY_CODE: "Add a student by invitation code",
      ADD_STUDENT: "Add student",
      ORDER_COPY_TRACKING: "Copy tracking number",
      ORDER_TRACK: "Track parcel",
      APP_NAME: "Wise",
      SOCIAL_MEDIA: "Social Media",
      ACCOUNT_PROFILE: "Account Profile",
      DRAFT: "Draft",
      WAITING_DOC: "Waiting Document",
      IN_PROGRESS: "In Progress",
      HISTORY: "History",
      ATTACHMENTS: "Attachments",
      DOC_NO: "Doc No",
      DATE: "Date",
      DOCUMENT_NO: "Document No",
      TIMELINE: "Timeline",
      ISSUE_NO: "Issue No",
      SUBJECT: "Subject",
      DEAR: "Dear",
      NAME_TITLE: "Name Title",
      NAME: "Name",
      FIRST_NAME: "First Name",
      LAST_NAME: "Last Name",
      NICKNAME: "Nickname",
      ACADEMY: "Academy",
      TYPE: "Type",
      PHONE: "Phone",
      DOCUMENT_CREATER: "Document Creater",
      DOB: "Date of birthday",
      DATE_FORMAT: "DD/MM/YYYY",
      YEAR: "Year",
      MONTH: "Month",
      DAY: "Day",
      TOPIC: "Topic",
      FILE_NAME: "File Name",
      FILE_PLACEHOLDER: "Browse or drop file here",
      FILE_PLACEHOLDER_ADD_ON: "Support PDF file",
      FILE_PLACEHOLDER_2: "Drag and drop file here. Or",
      UPLOADED_FILE: "Uploaded File",
      REMARK: "Remark",
      BY: "by",
      TEL: "Tel.",
      PASS: "Pass",
      FAIL: "Fail",
      CURRENT_INFORMATION: "Current Information",
      FULL_NAME: "Full Name",
      SEARCH_PLACEHOLDER: "Search...",
      SEARCH_USER_PLACEHOLDER: "Search...",
      SEARCH_PLACEHOLDER_TRACKING: "Search...",
      EMAIL: "Email",
      GENERAL_INFORMATION: "General Information",
      CONTACT_INFORMATION: "Contact Information",
      PLACE: "Place",
      START_TIME: "Start Time",
      END_TIME: "End Time",
      EVENT_TITLE: "Event Title",
      TOPIC_NAME: "Topic Name",
      PLEASE_ATTACH_DOCUMENTATION: "Please attach documentation",
      ADD: "Add",
      TIME_FORMAT: "Time HH:MM",
      TIME: "HH:MM",
      LOCATION: "Location",
      NOTE: "Note",
      REJECT: "Reject",
      WAITING: "Waiting",
      APPROVE: "Approve",
      COMMENT: "Comment",
      DETAIL: "Detail",
      APPROVED: "Approved",
      //
      INLINE_SEARCH: "Search:",
      YES: "Yes",
      NO: "No",
      NO_EVENTS: "No events",
      NO_ITEMS: "No Items",
      NO_ITEMS_FOUND: "No Items Found",
      NO_FILE_UPLOAD: "No File Uploaded",
      NO_REVISED_FILE: "No Revised File",
      NO_COMMENTS: "No Comments",

      SLOT_TIMES: "Slot times",
      PLEASE_COMMENT: "Please comment",

      USER: "User List",

      LOADING: "Loading",
      PROCESSING: "Processing",
      DOC_NO2: "Doc No.",

      // REPORTS
      DATE_PERIOD: "Date",
      MONTH_PERIOD: "Month",
      YEAR_PERIOD: "Year",
      INLINE_DATE_PERIOD: "Date Period:",
      INLINE_DATE: "Date:",
      INLINE_MONTH: "Month:",
      INLINE_YEAR: "Year:",
      TO: "To",
      START_DATE: "Start Date",
      END_DATE: "End Date",

      ITEMS: "Items",

      TEACHER: "TEACHER",
      WELCOME: "Welcome",

      BAHT: "Baht",
      PAY: "Pay",
      CANCEL_ORDER: "Cancel order",
      COURSE_DISCOUNT: "Discount",
      TOTAL_PRICE: "Total",
      AMOUNT: "Amount",
      COURSE_REVIEWS: "Reviews",
      ENROLL: "Enroll",
      ADD_CART: "Add cart",
      CART_EMPTY: "Your cart is empty",
      ERROR_404_TITLE: "We can't find that page",
      ERROR_404_BODY: "The link may have moved, or the address has a typo in it. Try the home page or the course list.",
      ERROR_500_TITLE: "Something went wrong",
      ERROR_500_BODY: "That is on our side. Please try again in a moment.",
      GO_HOME: "Back to home",
      CART_EMPTY_HINT: "Add the courses you are interested in and check out in one go.",
      COURSE_OPTIONS: "Options",
      DURATION: "Duration",
      VIDEO_DURATION_EXTRA: "Video Duration Extra",
      COURSE_VIDEO: "Video",
      EXAMPLE: "Example",
      POST_EXAMPLE: "Post Example",
      COURSE_DETAIL: "Course Detail",

      BROWSE_IMAGE: "Browse",

      OLD_PASSWORD: "Old Password",
      RESET_PASSWORD: "Reset Password",
      CHANGE_PASSWORD: "เปลี่ยนรหัสผ่าน",
      PASSWORD_CONFIRMATION: "Password Confirmation",
      NEW_PASSWORD: "New Password",
      NEW_PASSWORD_CONFIRMATION: "New Password Confirmation",
      NEW_PASSWORD_REQUIRED: "กรุณาตั้งรหัสผ่านใหม่เพื่อดำเนินการ",

      COURSES: "คอร์สเรียน",
      STUDENT: "นักเรียน",
      BUY_NOW: "Buy Now",
      STUDENT_REVIEWS: "รีวิวจากน้องๆ",

      DETAILS: "Details",
      CATEGORIES: "Categories",
      SEARCH: "Search",
      BLOG: "Blog",
      SEARCH_BLOG: "Search Blog",
      TOTAL_BLOG: "Total Blog",
      PUBLIC: "Public",
      RECENT_BLOG: "Recent Blogs",
    },

    LANDING_PAGE: {
      ONLINE_COURSE: "เรียนง่าย เข้าใจง่าย",
      BANNER_VISION: {
        LINE_1: "เรียนง่าย เข้าใจง่าย",
        LINE_2: "แก้จุดอ่อน เพิ่มจุดแข็ง",
        LINE_3: "โจทย์ไหนก็ง่ายนิดเดียว",
        LINE_4:
          "เรียนจากที่ใหนก็ได้ เรียนในเวลาและสถานที่ ที่เหมาะกับตารางเวลาของคุณ",
        LINE_5: "ปรับเปลี่ยนได้ตามใจต้องการ",
      },
      MARKS: {
        VIDEO: "Video lessons",
        QUIZ: "Auto-graded practice",
        REPORT: "Per-student skill report",
      },
      CTA: {
        EYEBROW: "See for yourself",
        HEAD_1: "Try it and you will see that",
        HEAD_2: "per-student teaching",
        HEAD_3: "actually works",
        BODY_1:
          "This does not stop at serving video. It records how every student answers, then shows the teacher who is weak on what.",
        BODY_2:
          "Students see what to revise next. All of it is clickable in this demo — no more guessing.",
      },
      COURSE_CATEGORY: "หมวดหมู่คอร์สเรียน",
      COURSE_CATEGORY_DESC:
        "ถ้ายังไม่สามารถตัดสินใจได้ว่าจะเลือกคอร์สเรียนออนไลน์ไหน เรามีตัวเลือกหมวดหมู่คอร์สเรียนที่เหมาะกับคุณ",
      MORE_COURSE: "ดูคอร์สทั้งหมด",
      SEE_COURSES: "Browse this category",
      WHY_WISE: "ทำไมต้องเรียนกับเรา",

      OUR_COURSE: "คอร์สเรียนของเรา",
    },
    ABOUT_US: {
      ABOUT: {
        PARAGRAPH_1:
          "We recognize the problems that many students encounter when learning English\nwhich is memorization without understanding, often using traditional learning methods\nBut here it's different! Because our teachers are not just instructors\nwith over 10 years of experience, but they understand the problems students are facing\nWe have summarized and created techniques to make English easy\nnot just memorization",
        PARAGRAPH_2:
          "We want students to give themselves a chance\nto try learning English the right way",
        LINE_1:
          "We recognize the problems that many students encounter when learning English",
        LINE_2:
          "which is memorization without understanding, often using traditional learning methods",
        LINE_3:
          "But here it's different! Because our teachers are not just instructors",
        LINE_4:
          "with over 10 years of experience, but they understand the problems students are facing",
        LINE_5:
          "We have summarized and created techniques to make English easy",
        LINE_6: "not just memorization",
        LINE_7: "We want students to give themselves a chance",
        LINE_8: "to try learning English the right way",
      },
      TEACHER: {
        TITLE: "Teacher on this demo",
        TITLE_DEFAULT: "Our teacher",
        FULLNAME: "Sample teaching team",
        NICKNAME: "Demo",
      },
    },
    BLOG: {
      FILTER: {
        CATEGORY: "Category",
        SEARCH: "Search",
        SEARCH_PLACEHOLDER: "Search...",
      },
    },
    PURCHASE_ORDER: {
      TITLE: "My Purchase Order",
    },
    AUTH: {
      OR: "or",
      WELCOME_TEXT: "Welcome to",
      WELCOME_DESC: "Please sign-in to your account for start using the system",
      USERNAME: "Username",
      EMAIL: "Email",
      PASSWORD: "Password",
      FORGOT_PASSWORD: "Forgot Password ?",
      ASIDE: {
        SIGN_IN_TITLE: "Welcome back to",
        SIGN_IN_BODY: "Sign in to pick up your classroom, your practice and your skills report.",
        SIGN_UP_TITLE: "Create an account with",
        SIGN_UP_BODY: "Sign up to keep your progress, practise as much as you like, and see what you are good at.",
        FORGOT_TITLE: "Forgotten password",
        FORGOT_BODY: "Give us the email you signed up with and we will send a link to set a new password.",
      },
      DEMO_HINT: "This is a demo — look around with a sample account, no sign-up needed.",
      DEMO_AS_STUDENT: "Enter as a student",
      DEMO_AS_PARENT: "Enter as a parent",
      SECTION_ACCOUNT: "Account",
      SECTION_PROFILE: "About you",
      SECTION_ADDRESS: "Address",
      PRIVACY_CONSENT_PREFIX: "I have read and accept the",
      PRIVACY_CONSENT_REQUIRED: "You must accept the Privacy Policy to sign up.",
      POLICY: "By signing up you accept this demo's terms of use.",
      NOT_REGISTER: "Not a Member yet?",
      ALREADY_REGISTER: "Already have an Account?",
      SIGN_IN: "Sign In",
      SIGN_UP: "Sign up",
      SUBMIT_SIGN_UP: "Submit",
      SIGN_OUT: "Sign Out",
      SIGN_INING: "Please wait",
      REGISTERING: "Please wait",
      REGISTER_ERROR: "The registration details are incorrect",
      INVALID_LOGIN:
        "The username or password you entered is not valid. Please double-check and try again.",
      INVALID_VERIFY:
        "The username or password you entered is not valid or don't have permission. Please double-check and try again.",
      WAIT_APPROVE:
        "This account is waiting for administrator approval. Please contact support if it takes too long.",
      ACCOUNT_REJECTED:
        "This account has been rejected. Please contact support.",
      GENERAL: {
        OR: "Or",
        SUBMIT_BUTTON: "Submit",
        NO_ACCOUNT: "Don't have an account?",
        SIGNUP_BUTTON: "Sign Up",
        FORGOT_BUTTON: "Forgot Password",
        BACK_BUTTON: "Back",
        PRIVACY: "Privacy",
        LEGAL: "Legal",
        CONTACT: "Contact",
      },
      LOGIN: {
        TITLE: "Login Account",
        BUTTON: "Sign In",
      },
      FORGOT: {
        TITLE: "Forgotten Password?",
        DESC: "Enter your email to reset your password",
        SUCCESS: "Your account has been successfully reset.",
      },
      REGISTER: {
        TITLE: "Sign Up",
        DESC: "Enter your details to create your account",
        SUCCESS: "Your account has been successfuly registered.",
        NOT_REGISTER: "You aren't create an account?",
      },
      INPUT: {
        EMAIL: "Email",
        FULLNAME: "Sample instructors",
        PASSWORD: "Password",
        CONFIRM_PASSWORD: "Confirm Password",
        USERNAME: "Username",
      },
      VALIDATION: {
        INVALID: "{{name}} is not valid",
        REQUIRED: "{{name}} is required",
        MIN_LENGTH: "{{name}} minimum length is {{min}}",
        AGREEMENT_REQUIRED: "Accepting terms & conditions are required",
        NOT_FOUND: "The requested {{name}} is not found",
        INVALID_LOGIN:
          "The username or password you entered is not valid. Please double-check and try again.",
        REQUIRED_FIELD: "Required field",
        MIN_LENGTH_FIELD: "Minimum field length:",
        MAX_LENGTH_FIELD: "Maximum field length:",
        INVALID_FIELD: "Field is not valid",
      },
    },
    COMPONENT: {
      ADD: "Add",
      CREATE: "Create",
      EDIT: "Edit",
      DELETE: "Delete",
      VIEW_DATA: "View Data",
      DOWNLOAD: "Download",
      UPLOAD: "Upload",
      BROWSE_FILE: "Browse File",
      PRINT: "Print",
      SAVE: "Save",
      SAVE_DRAFT: "Save As Draft",
      VIEW_DETAIL: "View Detail",
      SAVE_CHANGES: "Save Changes",
      NEW_EVENT: "New Event",
      CONFIRM: "Confirm",
      ACCEPT: "Accept",
      REJECT: "Reject",
      DISAPPROVE: "Disapprove",
      APPROVE: "Approve",
      APPROVED: "Approved",
      CONFIRM_REJECT: "Confirm Reject",
      CONFIRM_DISAPPROVE: "Confirm Disapprove",
      SUBMIT: "Submit",
      VIEW_COMMENT: "View Comment",
      APPROPRIATE: "Appropriate",
      MUST_REVISE: "Must Revise",
      SEND_EMAIL: "Send Email",
      ISSUE_A_CERTIFICATE: "Issue a Certificate",
      CANCEL: "Cancel",
      CLOSE: "Close",
      NEXT: "Next",
      IMPORT_DATA: "Import Data",
      IMPORT_PERSONNEL_DATA: "Import Personnel Data",
      FILE_FORMAT: "File Format",
    },
    VALIDATOR: {
      REQUIRED: "Field cannot be blank",
      REQUIRED_DATE: "Must be at least 1 Day",
      MUST_BE_AT_LEAST_X_CHARACTERS: "Must be at least {{value}} characters",
      MUST_CONTAIN_AT_LEAST_X_LETTER_IN_LOWER_CASE:
        "Must contain at least {{value}} letter in lower case",
      MUST_CONTAIN_AT_LEAST_X_LETTER_IN_UPPER_CASE:
        "Must contain at least {{value}} letter in upper case",
      MUST_CONTAIN_AT_LEAST_X_NUMBER: "Must contain at least {{value}} number",
      INVALID_EMAIL: "Please enter a valid email address",
      PASSWORD_MISMATCH: "Those passwords didn't match",
      PASSWORD_INVALID: "Invalid 'Password' pattern.",
      PLEASE_SELECT: "Please select",
      PLEASE_SELECT_TIME_SLOTS: "Please select time slots",
      PLEASE_SELECT_FILE: "Please select file",
      PLEASE_ADD_ALL_DOCUMENTS: "Please Add All Documents",

      DUPLICATE_DOC_NO:
        "The document number already exists in the system, please enter it again.",
    },
    SWEETALERT: {
      CONFIRMATION_TITLE: "Confirmation",
      CONFIRMATION_TEXT: "Are you sure you want to proceed this?",
      CONFIRMATION_DELETE_TEXT: "Are you sure you want to delete this?",
      CONFIRMATION_SAVE_TITLE: "Confirmation",
      CREATE_DATA: "Create Data",
      CREATE_DATA_TEXT: "Are you sure to create this data?",
      UPDATE_DATA: "Update Data",
      UPDATE_DATA_TEXT: "Are you sure to update this data?",
      SUCCESS_TITLE: "Success",
      SUCCESS_TEXT: "New information has been created.",
      SUCCESS_TEXT_UPDATE: "We have successfully updated the information.",
      SUCCESS_TEXT_DELETE: "We have successfully deleted this information.",
      ERROR_TITLE: "Oops",
      ERROR_TEXT: "Sorry, something went wrong.",
      WARNING_TITLE: "Warning",
      WARNING_TEXT:
        "Something is not right, It may prevent you from continuing.",
      WARNING_IMAGE_SIZE_TITLE: "Image is too large",
      WARNING_IMAGE_SIZE_TEXT:
        "Please make sure the image size is not larger than {{value}} {{unit}}",

      ERROR_APPROVER_DUPLICATE_OWNER: "Owner can not be approver",
      ERROR_COMMITTEE_DUPLICATE: "You have already elected this director.",
      ERROR_COMMITTEE_EXTERNAL: "External committee must contain at least 1",
      ERROR_COMMITTEE_INTERNAL: "Internal committee must contain at least 2",

      ERROR_NOTFOUND_DOC: "Document number not found",
      ERROR_NOTFOUND_DOC_BY_NAME: "Document not found by name",
      ERROR_REQUIRED: "Field cannot be blank",
    },
    MISC: {
      HELLO: "Hello",
      ASSOC_PROF: "Associate Professor",
    },
    TABLE: {
      NO_DATA_DISPLAY: "No data to display",
      NO_DATA: "No data",
      TOTAL: "Total",
      SUMMARY_TOTAL: "Summary Total",
      TOTAL_RESULT: "Total",
      TOTAL_LIST: "",
      CUSTOMER_PATIENT: "Customer / Patient",
      AGE: "Age",
      AGE_YEAR: "Age (Year)",
      ID: "ID",
      PHONE: "Phone",
      EMERGENCY: "Emergency",
      LINE: "LINE",
      LAST_UPDATED: "Last Updated",
      LAST_LOGIN: "Last Login",
      OPTION: "Option",
      BY: "by",
      CONNECTED: "Connected",
      NOT_CONNECTED: "Not Connected",
      DOC_NO: "Doc No.",
      DATE: "Date",
      SUB_TOTAL: "Total Price",
      NET_PRICE: "Total Price",
      PAID_PRICE: "Total",
      STATUS: "Status",
      PAID: "Paid",
      REFERENCE: "Reference",
      ISSUED_BY: "Issues by",
      ITEMS: "Items",
      PRICE_PER_UNIT: "Price/Unit",
      AMOUNT: "Amount",
      DISCOUNT: "Discount",
      BAHT: "Baht",
      MEDICINE_USAGE: "Medicine Usage",
      VAT: "Vat. ({{value}})",
      SERVICE_DATE: "Service Date",
      COMPLETED_DATE: "Completed Date",
      EXAMINATION_ROOM: "Examination Room",

      // REPORT
      RECEIPT_NO: "Receipt No",
      APPOINTMENT: "Appointment",

      // SETTING
      CODE: "Code",
      PRICE: "Price",
      UNIT: "Unit",
      NUMBER_OF_MEDICINE: "No. of Med.",
      ACCOUNT_NAME: "Account Name",
      ACCOUNT_NO: "Account No.",
      USED_FOR: "Used For",
      NAME: "Name",
      NAME_CODE: "Name / Code",
      LICENSE_NO: "License No.",
      EMAIL: "Email",
      ROLE: "Role",
      FREE: "Free",
      TRIAL_STARTED: "Trial started",
      TRIAL_STARTING: "Starting…",
      TRIAL_ONLY: "Free-trial courses only",
      TRIAL_BADGE: "Free trial",
      TRIAL_STUDENT_ONLY: "Free trials are for students only",
      FREE_TRIAL: "Free trial",
      DESCRIPTION: "Description",
      GROUP: "Group",
      CATEGORY: "Category",
      TYPE: "Type",
      UNIT_NAME: "Unit Name",
      PRIMARY_UNIT: "Primary Unit",
      MULTIPILIER: "Amt./Unt.",
      TIMES: "Times",
      SELL_PRICE: "Sell Price",
      TYPE_OF_SERVICE: "Type of Service",
      SHOW: "Show",
      ENTRIES: "Entries",
      SEARCH: "Search",

      // REPORT
      OWNER_NAME: "Name",
      Q_TIMES: "Times",
      Q_PEOPLE: "People",

      PAGINATION_LIMIT_SHOWING:
        "Showing {{start_value}}  -  {{end_value}}  of  {{total_value}} entries",
    },
    MANUAL: {
      BADGE: "User manual",
      TITLE: "Everything you need, in ten minutes",
      SUBTITLE:
        "Every step with real screenshots. Read it topic by topic, or watch the short video for each one.",
      MODE: "Manual format",
      MODE_READ: "Read",
      MODE_VIDEO: "Watch",
      SEARCH: "Search the manual, e.g. quiz, cart, password",
      EXPAND_ALL: "Expand all",
      COLLAPSE_ALL: "Collapse all",
      CONTENTS: "Contents",
      STEPS: "steps",
      STEP: "Step",
      TRY_IT: "Try it now",
      WATCH_INSTEAD: "Watch instead",
      READ_INSTEAD: "Read the details",
      POINT: "Point",
      OUTRO_TITLE: "The written manual has every step",
      OUTRO_BODY: "Menu: User manual — step by step, with a screenshot of each screen",
      IN_THIS_VIDEO: "In this video",
      NO_RESULTS: "Nothing matched. Try another word, such as quiz or parent.",
      M: {
        GETTING_STARTED: {
          TITLE: "Getting started",
          DESC: "Your first visit, signing in, and creating an account",
          VIDEO: "From opening the site to being signed in.",
          S: {
            HOME: {
              TITLE: "The home page",
              BODY: "Everything is reachable from the top menu. You can browse courses and articles before you have an account.",
              MARKS: {
                nav: "Main menu: courses, articles and about us",
                cta: "Shortcut to the full course list",
                login: "Sign in, always top right",
              },
            },
            SIGN_IN: {
              TITLE: "Signing in",
              BODY: "There is no separate login page. The form opens as a dialog when you reach a page that needs an account.",
              MARKS: {
                email: "Your account email and password",
                demo: "One-click demo login: a sample account, no sign-up. Demo system only.",
                forgot: "Forgot your password? We email you a reset link.",
                register: "No account yet? Register from here.",
              },
            },
            REGISTER: {
              TITLE: "Creating an account",
              BODY: "Fill in your details and choose a role. Students study and practise; parents follow their child's progress.",
              MARKS: {
                role: "Register as a student or as a parent",
                submit: "Confirm to create the account",
              },
            },
            SIGNED_IN: {
              TITLE: "Once you are signed in",
              BODY: "The menu gains Online class, Quizzes and My skills, and your account icon appears top right.",
              MARKS: {
                avatar: "Account menu: profile, orders, sign out",
                cart: "Cart, with the number of courses you picked",
                ribbon: "Demo banner: all content here is sample data",
              },
            },
          },
        },
        COURSES: {
          TITLE: "Finding a course",
          DESC: "Browse courses, read the details, try a lesson free",
          VIDEO: "How to find a course and read it before deciding.",
          S: {
            LIST: {
              TITLE: "All courses",
              BODY: "Courses are grouped by category. Each card shows the title, the teacher and the price.",
              MARKS: {
                card: "A course card — open it for the details",
                filter: "Category filter, to narrow the list",
              },
            },
            DETAIL: {
              TITLE: "Course details",
              BODY: "Everything you need before deciding: description, teacher, lesson count, how long you get, and what is included.",
              MARKS: {
                title: "Course title and learner rating",
                enroll: "Enrol now — straight to payment, skipping the cart",
                cart: "Add to cart, to buy several courses at once",
                trial: "Free trial — watch a sample lesson without buying",
              },
            },
          },
        },
        PURCHASE: {
          TITLE: "Buying a course",
          DESC: "The cart, payment, and your order history",
          VIDEO: "From adding to the cart to getting access.",
          S: {
            CART: {
              TITLE: "The cart",
              BODY: "The cart slides in from the right. Check the items and apply a discount code before paying.",
              MARKS: {
                items: "Courses in the cart — remove one with the bin icon",
                notice: "In the demo, checkout is simulated: no money moves",
                pay: "Pay to continue",
              },
            },
            ORDERS: {
              TITLE: "Order history",
              BODY: "Every order is kept here with its status: awaiting payment, complete, or cancelled.",
              MARKS: {
                table: "Orders, newest first",
                row: "Open a row for that order's details",
              },
            },
            ORDER_DETAIL: {
              TITLE: "Order details",
              BODY: "What the order contained, the total, and where the payment stands.",
              MARKS: {
                detail: "Items and amount due",
                code: "Order reference, for when you contact support",
              },
            },
          },
        },
        ONLINE_CLASS: {
          TITLE: "The online classroom",
          DESC: "Entering a course, the video player, and course files",
          VIDEO: "How to enter a course and use the player.",
          S: {
            DASHBOARD: {
              TITLE: "My courses",
              BODY: "Every course you have access to, with how far through it you are.",
              MARKS: {
                heading: "The My courses heading",
                card: "A course card with progress and time left",
                enter: "Enter the classroom",
              },
            },
            START: {
              TITLE: "Before the lesson starts",
              BODY: "Access is time-limited and the clock starts when you enter. Pause it if you are not ready to continue.",
              MARKS: {
                enter: "Enter the course to begin",
                pause: "Pause your access and stop the clock",
              },
            },
            WATCH: {
              TITLE: "Watching a lesson",
              BODY: "Pick a lesson from the list on the right. Your position is remembered, and course files sit in the tabs below.",
              MARKS: {
                player: "The player — speed and fullscreen are in the controls",
                chapters: "Lessons, with the length of each",
                tabs: "About this lesson, and PDF files",
              },
            },
            LESSON_QUIZZES: {
              TITLE: "Quizzes for the lesson",
              BODY: "The course's quizzes sit under the lesson. Start one as soon as the video ends.",
              MARKS: {
                quizzes: "Quizzes attached to this lesson",
              },
            },
          },
        },
        QUIZ: {
          TITLE: "Quizzes",
          DESC: "Sit a quiz, get it marked instantly, read the worked answers",
          VIDEO: "One quiz from the first question to the result.",
          S: {
            LIST: {
              TITLE: "Choosing a quiz",
              BODY: "Every quiz from the courses you are enrolled in, with question count, pass mark and your best score.",
              MARKS: {
                list: "The full list of quizzes",
                start: "Start, to open the first question",
                best: "Your best score so far",
              },
            },
            DRILLS: {
              TITLE: "My drills",
              BODY: "Drills are built from the topics you are weakest at. Repeat them as often as you like — the questions change each time.",
              MARKS: {
                drills: "Drills built from your own weak topics",
              },
            },
            QUESTION: {
              TITLE: "Sitting the quiz",
              BODY: "One question at a time. Pick an answer and continue; you can always go back and change it.",
              MARKS: {
                index: "Where you are, and the progress bar",
                body: "The question, tagged with its topic",
                choice: "Tap an option to answer",
                advance: "Next, or back to the previous question",
              },
            },
            REVIEW: {
              TITLE: "Review before submitting",
              BODY: "The review screen shows what you have left blank. Tap a question number to go back to it.",
              MARKS: {
                heading: "The review screen",
                unanswered: "A warning about unanswered questions",
                submit: "Submit when you are ready",
              },
            },
            RESULT: {
              TITLE: "Your result",
              BODY: "Marked the moment you submit: your overall score, and how you did at each difficulty.",
              MARKS: {
                score: "Overall percentage, and whether you passed",
                difficulty: "Results split by question difficulty",
              },
            },
            BREAKDOWN: {
              TITLE: "Results by topic",
              BODY: "Which topics you handled and which you missed — the same data that drives your skill summary.",
              MARKS: {
                skills: "Share correct in each topic",
                skillslink: "Link through to My skills",
              },
            },
            ANSWER_KEY: {
              TITLE: "Answers and explanations",
              BODY: "Every question comes with the right answer and why it is right — and where yours went wrong.",
              MARKS: {
                answers: "Your answer against the correct one, question by question",
                misconception: "The mistake this question is designed to catch",
              },
            },
          },
        },
        SKILLS: {
          TITLE: "My skills",
          DESC: "What you are good at, what to revise, and what to do next",
          VIDEO: "Reading your skill analysis and drilling a weak topic.",
          S: {
            OVERVIEW: {
              TITLE: "The overview",
              BODY: "Built from your quiz history, weighted towards recent attempts — so a topic you have just fixed is not dragged down by old ones.",
              MARKS: {
                heading: "The My skills page",
                narrative: "The summary in plain words",
                ability: "Totals: questions answered, strong topics, topics to revise",
              },
            },
            WEAK: {
              TITLE: "What to revise",
              BODY: "Weak topics are listed with their recent numbers. One press builds a drill on exactly that topic.",
              MARKS: {
                topic: "A topic to revise, with its practise button",
                misconceptions: "Mistakes you keep repeating",
                stale: "A badge for a topic you have not revisited in a while",
              },
            },
            NEXT: {
              TITLE: "What to do next",
              BODY: "Concrete next steps: the lesson to rewatch, and courses that fit the gaps.",
              MARKS: {
                recommendations: "Your recommended next steps",
                suggestions: "Courses suggested from your skill picture",
              },
            },
          },
        },
        PROFILE: {
          TITLE: "Profile and reports",
          DESC: "Study record, quiz history, account details, and linking a parent",
          VIDEO: "Your learning reports and your account settings.",
          S: {
            STUDY_RECORD: {
              TITLE: "Study record",
              BODY: "Your study time and activity, so you can see how steady you have been.",
              MARKS: {
                heading: "The study record page",
                chart: "Study time over recent days",
                activity: "Your latest activity",
                tabs: "Switch to quiz history and skills",
              },
            },
            QUIZ_RECORD: {
              TITLE: "Quiz history",
              BODY: "Every attempt is kept. Open one to see that result and its answers again.",
              MARKS: {
                record: "Attempts, with score and date",
                tabskills: "The skills tab, summarising all attempts",
              },
            },
            ACCOUNT: {
              TITLE: "Account and profile",
              BODY: "Change your name, email, phone and photo, then save.",
              MARKS: {
                save: "Save your changes",
              },
            },
            PARENT: {
              TITLE: "Linking a parent",
              BODY: "The student sends an invitation link. Once the parent opens it, they can follow that student's progress.",
              MARKS: {
                add: "This account's parent invitation link",
                code: "Copy the link and send it to your parent",
                unlink: "Unlink a parent account",
              },
            },
            PASSWORD: {
              TITLE: "Changing your password",
              BODY: "Enter the new password twice, then save.",
              MARKS: {
                save: "Save the new password",
              },
            },
          },
        },
        BLOG: {
          TITLE: "Articles",
          DESC: "Study tips and news from the teaching team",
          VIDEO: "Finding an article and reading on.",
          S: {
            LIST: {
              TITLE: "The article list",
              BODY: "Newest first — tap a headline to read it.",
              MARKS: {
                search: "Search by keyword, or browse by category",
                row: "The rest of the articles",
              },
            },
            DETAIL: {
              TITLE: "Reading an article",
              BODY: "The full article, with related ones alongside.",
              MARKS: {
                article: "The article body",
              },
            },
          },
        },
      },
    },
  },
};
